/**
 * Reusable versioned canonical debug-fixture framework.
 *
 * Each canonical element type (Wall, Stair, …) can register one or more
 * `CanonicalFixture` records describing a variant (compliant, non-compliant,
 * missing input, invalid, unsupported schema version) for a given
 * `schemaVersion`. Consumers reach fixtures only through
 * `getFixture(elementType, variant, schemaVersion?)`; production request
 * normalisation on the Web API is never touched.
 *
 * The registry is intentionally decoupled from `mocks/canonicalMocks.ts`: the
 * mock catalogue continues to serve every element slug that has not yet been
 * migrated, while any element registered here overrides its default
 * (compliant) fixture with the registry copy. New element schemas can be
 * added by dropping a new file under `src/fixtures/canonical/` and calling
 * `registerFixture(...)` for each variant.
 */

import type { CanonicalElementPayload } from '../../mocks/canonicalMocks';

/** Standard fixture variants supported by the registry. */
export type FixtureVariant =
    | 'compliant'
    | 'noncompliant'
    | 'missing-input'
    | 'invalid'
    | 'unsupported-version';

export const FIXTURE_VARIANTS: readonly FixtureVariant[] = Object.freeze([
    'compliant',
    'noncompliant',
    'missing-input',
    'invalid',
    'unsupported-version',
]);

export interface FixtureMetadata {
    readonly description: string;
    readonly expectedRuleIds?: readonly string[];
    readonly expectedVerdict?: 'pass' | 'fail' | 'inconclusive';
    readonly expectedCitationUrns?: readonly string[];
    readonly expectedEvidence?: 'narrative' | 'type-error';
    readonly missingInput?: boolean;
    readonly invalid?: boolean;
}

export interface CanonicalPackageEntity {
    readonly id: string;
    readonly catalogueEntityId: 'cem.entity.wall' | 'cem.entity.stair' | 'cem.entity.ramp';
    readonly type: 'Wall' | 'Stair' | 'Ramp';
    readonly cemVersion: string;
    readonly provenance: Readonly<{
        sourceHost: string;
        sourceElementId: string;
        sourceVersion: string;
    }>;
    readonly properties: Readonly<Record<string, unknown>>;
}

/**
 * The current findings endpoint still reads a single canonical element from
 * the root. Versioned debug fixtures therefore retain that root facade while
 * also carrying the complete US229 package contract consumed by package-aware
 * validators. This shape is debug-only and never changes host production
 * routing.
 */
export interface CanonicalPackagePayload extends CanonicalElementPayload {
    readonly schemaVersion: string;
    readonly catalogueId: 'bimcomply.canonical-entities';
    readonly catalogueVersion: '1.0.0';
    readonly packageId: string;
    readonly runId: string;
    readonly cemVersion: string;
    readonly apiVersion: 'v2';
    readonly clientCapabilities: Readonly<{
        geometryLevels: readonly ['scalar'];
        scopes: readonly ['element'];
        streaming: true;
        hostId: 'web-plugin-debug';
        extractorVersion: '1.0.0';
    }>;
    readonly scope: Readonly<{
        requested: 'element';
        focusElements: readonly [string];
    }>;
    readonly entities: readonly [CanonicalPackageEntity];
    readonly relationships: readonly [];
}

export interface CanonicalFixture {
    readonly elementType: string;
    readonly variant: FixtureVariant;
    readonly schemaVersion: string;
    readonly compatibleSchemaVersions: readonly string[];
    readonly wrapperContext: Readonly<{
        BuildingType: string;
        PlaceName: string;
        Latitude: number;
        Longitude: number;
        NumberOfLevels: number;
    }>;
    readonly payload: CanonicalPackagePayload;
    readonly defaultElementType?: string;
    readonly metadata: FixtureMetadata;
}

export interface FixtureLookupOptions {
    readonly schemaVersion?: string;
    readonly synthesiseUnsupportedVersion?: boolean;
}
